import { useNavigate } from 'react-router-dom';

interface StateCardProps {
  state: {
    name: string;
    slug: string;
    capital: string;
    population: string;
    taxRate: string;
    taxType: string;
    region: string;
    cities: Array<{ name: string; slug: string }>;
  };
}

const StateCard = ({ state }: StateCardProps) => {
  const navigate = useNavigate();

  // Generate state-specific image with unique identifier
  const stateImage = `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg$%7BencodeURIComponent%28%20%20%20%20%20%60$%7Bstate.name%7D%20state%20landmark%20iconic%20scenery%20$%7Bstate.capital%7D%20professional%20landscape%20photography%20warm%20natural%20light%20beautiful%20vista%60%20%20%20%29%7D&width=800&height=450&seq=state-${state.slug}&orientation=landscape`;

  return (
    <article
      onClick={() => navigate(`/states/${state.slug}`)}
      className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1"
    >
      {/* State Image */}
      <div className="relative w-full h-48 overflow-hidden">
        <img
          src={stateImage}
          alt={`${state.name} state landscape`}
          title={`${state.name} - ${state.capital}`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          width="800"
          height="450"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-2xl font-bold text-white mb-1 drop-shadow-lg">
            {state.name}
          </h3>
          <div className="flex items-center gap-2 text-white/90 text-sm">
            <i className="ri-map-pin-line"></i>
            <span>{state.capital}</span>
          </div>
        </div>
        <div className="absolute top-3 right-3">
          <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm text-gray-800 text-xs font-semibold rounded-full whitespace-nowrap">
            {state.region}
          </span>
        </div>
      </div>

      {/* State Info */}
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <i className="ri-group-line"></i>
            <span>{state.population}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
              state.taxRate === '0%'
                ? 'bg-emerald-50 text-emerald-700'
                : parseFloat(state.taxRate) < 5
                ? 'bg-blue-50 text-blue-700'
                : 'bg-amber-50 text-amber-700'
            }`}>
              {state.taxRate === '0%' ? 'No Income Tax' : `${state.taxRate} ${state.taxType}`}
            </span>
          </div>
        </div>

        {/* Cities */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-500">Available Calculators:</span>
            <span className="text-xs font-semibold text-emerald-600">
              {state.cities.length} {state.cities.length === 1 ? 'city' : 'cities'}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {state.cities.slice(0, 3).map((city) => (
              <span
                key={city.slug}
                className="px-2 py-1 bg-gray-50 text-gray-700 text-xs rounded-md whitespace-nowrap"
              >
                {city.name}
              </span>
            ))}
            {state.cities.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md font-medium whitespace-nowrap">
                +{state.cities.length - 3} more
              </span>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600 font-medium">View State Details</span>
            <i className="ri-arrow-right-line text-emerald-600 group-hover:translate-x-1 transition-transform"></i>
          </div>
        </div>
      </div>
    </article>
  );
};

export default StateCard;
